import { Copy, Pencil, Trash2 } from "lucide-react";
import React from "react";

export interface VisitsBulkSelectionInlineActionsProps {
  selectedVisitCount: number;
  isBusy?: boolean;
  onBulkEditClick?: () => void;
  onBulkCopyClick?: () => void;
  onBulkDeleteClick?: () => void;
  /** Hides "Copy to" when the case has no other version to copy into. */
  hasOtherVersions?: boolean;
  /** Align with the sticky New Visit action row. */
  variant?: "default" | "actionBar";
}

/** Count + Edit/Delete/Copy for bulk visit selection. */
export const VisitsBulkSelectionInlineActions: React.FC<VisitsBulkSelectionInlineActionsProps> = ({
  selectedVisitCount,
  isBusy = false,
  onBulkEditClick,
  onBulkCopyClick,
  onBulkDeleteClick,
  hasOtherVersions = false,
  variant = "default",
}) => {
  if (selectedVisitCount <= 0) return null;

  const isActionBar = variant === "actionBar";
  const groupClass = isActionBar
    ? "inline-flex overflow-hidden rounded-xl border border-neutral-200 bg-card shadow-sm"
    : "inline-flex overflow-hidden rounded-lg border border-neutral-200 bg-card shadow-sm";
  const buttonClass = isActionBar
    ? "inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold transition-colors"
    : "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold transition-colors";
  const labelClass =
    "text-2xs font-semibold uppercase tracking-micro text-neutral-400 whitespace-nowrap";
  const countClass = isActionBar
    ? "text-sm font-semibold text-neutral-600 whitespace-nowrap"
    : "text-xs font-semibold text-neutral-600 whitespace-nowrap";

  return (
    <div
      className="flex flex-wrap items-center justify-end gap-2 rounded-xl bg-card pl-2 sm:gap-3"
      aria-label={`Bulk actions, ${selectedVisitCount} visit${selectedVisitCount !== 1 ? "s" : ""} selected`}
    >
      <span className={labelClass}>Bulk Actions</span>
      <span className={`hidden h-4 w-px bg-neutral-200 sm:block`} aria-hidden />
      <span className={countClass}>
        {selectedVisitCount} visit{selectedVisitCount !== 1 ? "s" : ""} selected
      </span>
      <div role="group" aria-label="Bulk visit actions" className={groupClass}>
        <button
          type="button"
          disabled={isBusy || !onBulkEditClick}
          className={`${buttonClass} border-r border-neutral-200 text-neutral-700 hover:bg-primary-subtle hover:text-primary-emphasis disabled:cursor-not-allowed disabled:opacity-40`}
          onClick={onBulkEditClick}
        >
          <Pencil size={isActionBar ? 16 : 14} strokeWidth={2.5} aria-hidden />
          Edit
        </button>
        <button
          type="button"
          disabled={isBusy || !onBulkDeleteClick}
          className={`${buttonClass} ${hasOtherVersions ? "border-r border-neutral-200" : ""} text-neutral-700 hover:bg-destructive-subtle hover:text-destructive-text disabled:cursor-not-allowed disabled:opacity-40`}
          onClick={onBulkDeleteClick}
        >
          <Trash2 size={isActionBar ? 16 : 14} strokeWidth={2.5} aria-hidden />
          Delete
        </button>
        {hasOtherVersions && (
          <button
            type="button"
            disabled={isBusy || !onBulkCopyClick}
            className={`${buttonClass} text-neutral-700 hover:bg-primary-subtle hover:text-primary-emphasis disabled:cursor-not-allowed disabled:opacity-40`}
            onClick={onBulkCopyClick}
          >
            <Copy size={isActionBar ? 16 : 14} strokeWidth={2.5} aria-hidden />
            Copy to
          </button>
        )}
      </div>
    </div>
  );
};
